import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { GALLERY_ITEMS, GALLERY_CATEGORIES } from "@/lib/data";
import { Reveal } from "@/components/Reveal";

const INITIAL = 6;

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [showAll, setShowAll] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const items = filter === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === filter);
  const visible = showAll ? items : items.slice(0, INITIAL);
  const countFor = (c) => (c === "All" ? GALLERY_ITEMS.length : GALLERY_ITEMS.filter((g) => g.category === c).length);

  const move = (dir) => {
    if (lightbox === null) return;
    const idx = items.findIndex((i) => i.id === lightbox);
    const next = (idx + dir + items.length) % items.length;
    setLightbox(items[next].id);
  };

  return (
    <section id="gallery" className="py-24 lg:py-32 bg-white" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="mb-10">
          <Reveal>
            <div className="text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold mb-2">Gallery</div>
            <h2 className="font-display font-bold text-medis-navy text-3xl sm:text-4xl lg:text-[30px] leading-[1.1] tracking-tight max-w-4xl">
              Inside MEDIS our work in action.
            </h2>
            <p className="mt-4 text-medis-gray text-[15.5px] leading-relaxed max-w-xl">
              A look at our installations, equipment, teams and facilities across India.
            </p>
          </Reveal>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {GALLERY_CATEGORIES.map((c) => {
            const isActive = filter === c;
            return (
              <button
                key={c}
                onClick={() => {
                  setFilter(c);
                  setShowAll(false);
                }}
                className="px-4 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 border"
                style={isActive ? { background: "#185FA5", color: "white", borderColor: "#185FA5" } : { background: "white", color: "#4b5563", borderColor: "#e5e7eb" }}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              >
                {c}
                <span
                  className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={isActive ? { background: "rgba(255,255,255,0.25)", color: "white" } : { background: "#f3f4f6", color: "#6b7280" }}
                >
                  {countFor(c)}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
          {visible.map((g, i) => (
            <motion.div
              key={g.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: Math.min((i % 3) * 0.08, 0.25) }}
              className="rounded-2xl overflow-hidden border border-medis-silver/20 cursor-pointer group shadow-sm hover:shadow-xl transition-shadow duration-300"
              onClick={() => setLightbox(g.id)}
              data-testid={`gallery-item-${g.id}`}
            >
              <div className="relative overflow-hidden h-[260px]">
                <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" draggable="false" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-medis-teal font-semibold mb-1">{g.category}</div>
                  <div className="text-white font-display font-semibold text-[15px]">{g.title}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {items.length > INITIAL && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => setShowAll((s) => !s)}
              className="px-6 py-3 rounded-full bg-medis-blue text-white text-[14px] font-semibold hover:bg-medis-blue-dark transition-colors"
              data-testid="gallery-show-more"
            >
              {showAll ? "Show Less" : `View More (${items.length - INITIAL} more)`}
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
              onClick={(e) => {
                e.stopPropagation();
                move(-1);
              }}
              aria-label="Previous image"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <motion.div key={lightbox} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              {items.filter((i) => i.id === lightbox).map((g) => (
                <div key={g.id}>
                  <img src={g.image} alt={g.title} className="w-full max-h-[78vh] object-contain rounded-xl" />
                  <div className="mt-3 text-white text-center">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-medis-teal font-semibold">{g.category}</div>
                    <div className="font-display font-semibold">{g.title}</div>
                  </div>
                </div>
              ))}
            </motion.div>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
              onClick={(e) => {
                e.stopPropagation();
                move(1);
              }}
              aria-label="Next image"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
